import { useEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";
import PropTypes from "prop-types";

/**
 * CopyableField — muestra un dato que el usuario necesita copiar literalmente
 * (un código, un importe, un número de teléfono) con acción de copiado y
 * confirmación visible.
 *
 * Cubre: valor vacío, disabled, foco de teclado, nombre accesible del botón y
 * feedback de éxito. El botón sólo aparece si hay valor que copiar.
 *
 * Props:
 *   label     — etiqueta visible sobre el valor
 *   value     — texto que se copia al portapapeles
 *   display   — texto alternativo a mostrar (por defecto, `value`)
 *   helpText  — texto auxiliar bajo el valor
 *   mono      — renderiza el valor en monoespaciado y con tracking (default true)
 *   emphasis  — resalta el valor con tamaño mayor
 *   disabled  — desactiva la acción de copiar
 *   emptyText — qué mostrar cuando no hay valor
 */
export default function CopyableField({
    label,
    value,
    display,
    helpText,
    mono = true,
    emphasis = false,
    disabled = false,
    emptyText = "Pendiente",
    className = "",
}) {
    const [copied, setCopied] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => () => clearTimeout(timerRef.current), []);

    const hasValue = Boolean(value);

    const handleCopy = async () => {
        if (!hasValue || disabled) return;
        try {
            await navigator.clipboard.writeText(String(value));
            setCopied(true);
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => setCopied(false), 2000);
        } catch {
            // Sin permiso de portapapeles el usuario todavía puede seleccionar
            // el texto a mano: no bloqueamos ni mostramos un error intrusivo.
            setCopied(false);
        }
    };

    return (
        <div className={`flex flex-col gap-1 min-w-0 ${className}`.trim()}>
            <span className="text-xs uppercase tracking-wide font-semibold text-base-content/55">
                {label}
            </span>
            <div className="flex items-center gap-2 min-w-0">
                <span
                    className={`min-w-0 break-all ${mono ? "font-mono tracking-wider" : ""} ${
                        emphasis ? "text-xl font-bold" : "font-medium"
                    } ${hasValue ? "" : "text-base-content/40 italic"}`.trim()}
                >
                    {hasValue ? (display ?? value) : emptyText}
                </span>
                {hasValue && (
                    <button
                        type="button"
                        onClick={handleCopy}
                        disabled={disabled}
                        aria-label={copied ? `${label} copiado` : `Copiar ${label}`}
                        title={copied ? "Copiado" : "Copiar"}
                        className="btn btn-ghost btn-xs btn-square shrink-0
                            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
                            disabled:opacity-40"
                    >
                        {copied
                            ? <Check className="w-3.5 h-3.5 text-success" />
                            : <Copy className="w-3.5 h-3.5" />}
                    </button>
                )}
            </div>
            {helpText && (
                <span className="text-xs text-base-content/55">{helpText}</span>
            )}
            <span aria-live="polite" className="sr-only">
                {copied ? `${label} copiado al portapapeles` : ""}
            </span>
        </div>
    );
}

CopyableField.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    display: PropTypes.node,
    helpText: PropTypes.node,
    mono: PropTypes.bool,
    emphasis: PropTypes.bool,
    disabled: PropTypes.bool,
    emptyText: PropTypes.node,
    className: PropTypes.string,
};
